import { useEffect, useState } from 'preact/hooks';
import { Ban, Check, Copy, MessageCircle, MoreHorizontal, Share2, UserPlus, UserRound, Users, UserX, X } from 'lucide-preact';
import type { Friend } from '@vm/shared';
import { copyText, shareText } from '../platform';
import { go, openModal, replace, route } from '../state/nav';
import {
  acceptFriend,
  blockUser,
  declineFriend,
  inviteToParty,
  loadFriends,
  openChat,
  removeFriend,
  sendFriendRequest,
  unblockUser,
} from '../state/socialActions';
import { friends, isOffline, me, party, presenceById, restricted, toast } from '../state/store';
import { timeAgo } from '../ui/format';
import { useBusy } from '../ui/hooks';
import { AsyncButton, Avatar, confirmDialog, Empty, ErrorState, Modal, OnlineOnly, presenceLabel, Screen, Spinner, Tabs } from '../ui/kit';
import { openProfile } from './Profile';

type Tab = 'friends' | 'requests' | 'add' | 'blocked';

function livePresence(f: Friend): { presence: Friend['presence']; activity?: string } {
  return presenceById.value[f.id] ?? { presence: f.presence, activity: f.activity };
}

function sortFriends(list: Friend[]): Friend[] {
  const rank = { in_match: 0, lobby: 1, away: 2, offline: 3 } as const;
  return [...list].sort((a, b) => rank[livePresence(a).presence] - rank[livePresence(b).presence] || a.name.localeCompare(b.name));
}

function FriendMenu(props: { f: Friend; close: () => void }) {
  const f = props.f;
  return (
    <div class="menu-list">
      <button
        class="menu-item"
        onClick={() => {
          props.close();
          openProfile(f.id, f.name);
        }}
      >
        <UserRound size={20} /> View profile
      </button>
      <button
        class="menu-item"
        onClick={async () => {
          props.close();
          if (await confirmDialog({ title: `Remove ${f.name}?`, body: 'You can add each other again later.', confirm: 'Remove', danger: true })) await removeFriend(f.id);
        }}
      >
        <UserX size={20} /> Remove friend
      </button>
      <button
        class="menu-item danger"
        onClick={async () => {
          props.close();
          if (await confirmDialog({ title: `Block ${f.name}?`, body: "They'll be removed from your friends and can't message or invite you.", confirm: 'Block', danger: true }))
            await blockUser(f.id, f.name);
        }}
      >
        <Ban size={20} /> Block
      </button>
    </div>
  );
}

function FriendRow(props: { f: Friend }) {
  const f = props.f;
  const live = livePresence(f);
  const inParty = party.value?.members.some((m) => m.id === f.id) ?? false;
  const online = live.presence !== 'offline';
  return (
    <div class={`frow ${online ? 'is-online' : ''}`}>
      <button class="frow-who" onClick={() => openProfile(f.id, f.name)}>
        <Avatar name={f.name} presence={live.presence} size={46} />
        <span class="frow-text">
          <b>
            {f.clanTag && <span class="frow-tag">[{f.clanTag}]</span>}
            {f.name}
            <span class="frow-level">Lv {f.level}</span>
          </b>
          <small class={`p-${live.presence}`}>{presenceLabel(live.presence, live.activity)}</small>
        </span>
      </button>
      <div class="frow-actions">
        {online && !inParty && (
          <AsyncButton class="sm green" onClick={() => inviteToParty(f.id, f.name)} title="Invite to party">
            <Users size={18} /> <span class="hide-sm">Invite</span>
          </AsyncButton>
        )}
        {inParty && <span class="frow-chip">In party</span>}
        {!restricted.value && (
          <button class="btn sm icon" title="Message" aria-label={`Message ${f.name}`} onClick={() => openChat(`dm:${f.id}`)}>
            <MessageCircle size={18} />
          </button>
        )}
        <button class="btn sm icon" aria-label="More" onClick={() => openModal((close) => <Modal title={f.name} size="sm" onClose={close}><FriendMenu f={f} close={close} /></Modal>)}>
          <MoreHorizontal size={18} />
        </button>
      </div>
    </div>
  );
}

function FriendsList() {
  const f = friends.value!;
  if (!f.friends.length) {
    return (
      <Empty icon={<Users size={44} />} title="No friends yet" body="Share your friend code or add someone by theirs.">
        <button class="btn primary" onClick={() => replace('friends', { tab: 'add' })}>
          <UserPlus size={18} /> Add a friend
        </button>
      </Empty>
    );
  }
  return (
    <div class="flist">
      {sortFriends(f.friends).map((x) => (
        <FriendRow key={x.id} f={x} />
      ))}
    </div>
  );
}

function Requests() {
  const f = friends.value!;
  if (!f.incoming.length && !f.outgoing.length) return <Empty icon={<UserPlus size={44} />} title="No requests" body="Friend requests you send or receive show up here." />;
  return (
    <div class="flist">
      {f.incoming.length > 0 && <h3 class="section-title">Wants to be your friend</h3>}
      {f.incoming.map((x) => (
        <div key={x.id} class="frow is-online">
          <button class="frow-who" onClick={() => openProfile(x.id, x.name)}>
            <Avatar name={x.name} size={46} />
            <span class="frow-text">
              <b>
                {x.name}
                <span class="frow-level">Lv {x.level}</span>
              </b>
              <small>{timeAgo(x.since)}</small>
            </span>
          </button>
          <div class="frow-actions">
            <AsyncButton class="sm green" onClick={() => acceptFriend(x.id, x.name)}>
              <Check size={18} /> Accept
            </AsyncButton>
            <AsyncButton class="sm" onClick={() => declineFriend(x.id)}>
              <X size={18} /> <span class="hide-sm">Decline</span>
            </AsyncButton>
          </div>
        </div>
      ))}
      {f.outgoing.length > 0 && <h3 class="section-title">Waiting for an answer</h3>}
      {f.outgoing.map((x) => (
        <div key={x.id} class="frow">
          <div class="frow-who">
            <Avatar name={x.name} size={46} />
            <span class="frow-text">
              <b>
                {x.name}
                <span class="frow-level">Lv {x.level}</span>
              </b>
              <small>Sent {timeAgo(x.since)}</small>
            </span>
          </div>
          <div class="frow-actions">
            <AsyncButton class="sm" onClick={() => removeFriend(x.id)}>
              Cancel
            </AsyncButton>
          </div>
        </div>
      ))}
    </div>
  );
}

function AddFriend() {
  const code = me.value?.code ?? '';
  const [value, setValue] = useState('');
  const [busy, run] = useBusy();
  const v = value.trim();
  const isCode = /^VM-?[A-Z0-9]{4,8}$/i.test(v);
  const submit = (): Promise<void> =>
    run(async () => {
      if (!v) return;
      const ok = await sendFriendRequest(isCode ? { code: v.toUpperCase() } : { name: v });
      if (ok) setValue('');
    });
  return (
    <div class="addfriend">
      <div class="addfriend-code">
        <span class="muted">Your friend code</span>
        <div class="addfriend-code-row">
          <b class="code-big">{code || '—'}</b>
          <button
            class="btn icon"
            aria-label="Copy friend code"
            onClick={async () => {
              if (await copyText(code)) toast('Friend code copied', { kind: 'success' });
            }}
          >
            <Copy size={20} />
          </button>
          <button
            class="btn icon"
            aria-label="Share friend code"
            onClick={async () => {
              const r = await shareText('Voxel Marines', `Add me in Voxel Marines! My friend code is ${code}`);
              if (r === 'copied') toast('Invite copied — paste it to a friend', { kind: 'success' });
            }}
          >
            <Share2 size={20} />
          </button>
        </div>
        <small class="muted">Only share it with people you know.</small>
      </div>
      <form
        class="addfriend-form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label class="ui-field-label">Add by friend code or exact name</label>
        <div class="ui-input-row">
          <input class="ui-input big" value={value} maxLength={20} placeholder="VM-XXXXX or name" autoComplete="off" spellcheck={false} onInput={(e) => setValue((e.currentTarget as HTMLInputElement).value)} />
          <button class="btn primary" type="submit" disabled={!v || busy}>
            <UserPlus size={20} /> Send
          </button>
        </div>
      </form>
    </div>
  );
}

function Blocked() {
  const list = friends.value!.blocked;
  if (!list.length) return <Empty icon={<Ban size={44} />} title="Nobody blocked" body="Players you block can't message you, invite you or send friend requests." />;
  return (
    <div class="flist">
      {list.map((b) => (
        <div key={b.id} class="frow">
          <div class="frow-who">
            <Avatar name={b.name} size={46} />
            <span class="frow-text">
              <b>{b.name}</b>
            </span>
          </div>
          <div class="frow-actions">
            <AsyncButton class="sm" onClick={() => unblockUser(b.id, b.name)}>
              Unblock
            </AsyncButton>
          </div>
        </div>
      ))}
    </div>
  );
}

export function FriendsScreen() {
  const tab = (route.value.params.tab as Tab) || 'friends';
  const [error, setError] = useState<string | null>(null);
  const load = (): void => {
    setError(null);
    loadFriends().catch((e: Error) => setError(e.message));
  };
  useEffect(() => {
    if (!isOffline.value) load();
  }, []);
  const f = friends.value;
  return (
    <Screen
      title="Friends"
      icon={<Users size={26} />}
      tabs={
        <Tabs<Tab>
          value={tab}
          onChange={(t) => replace('friends', { tab: t })}
          tabs={[
            { id: 'friends', label: 'Friends', badge: 0 },
            { id: 'requests', label: 'Requests', badge: f?.incoming.length ?? 0 },
            { id: 'add', label: 'Add' },
            { id: 'blocked', label: 'Blocked' },
          ]}
        />
      }
    >
      <OnlineOnly feature="Friends">
        <div class="scr-scroll narrow" key={tab}>
          {tab === 'add' ? (
            <AddFriend />
          ) : !f ? (
            error ? (
              <ErrorState message={error} onRetry={load} />
            ) : (
              <Spinner label="Loading friends…" />
            )
          ) : tab === 'friends' ? (
            <FriendsList />
          ) : tab === 'requests' ? (
            <Requests />
          ) : (
            <Blocked />
          )}
        </div>
      </OnlineOnly>
    </Screen>
  );
}

// Party invite picker ------------------------------------------------------------------------------------------------

function PartyInvite(props: { close: () => void }) {
  useEffect(() => {
    void loadFriends().catch(() => {});
  }, []);
  const f = friends.value;
  if (!f) return <Spinner label="Loading friends…" />;
  const list = sortFriends(f.friends);
  const members = new Set(party.value?.members.map((m) => m.id) ?? []);
  if (!list.length) {
    return (
      <Empty icon={<Users size={40} />} title="Add some friends first" body="Party up with friends to queue together.">
        <button
          class="btn primary"
          onClick={() => {
            props.close();
            go('friends', { tab: 'add' });
          }}
        >
          <UserPlus size={18} /> Add friends
        </button>
      </Empty>
    );
  }
  return (
    <div class="flist compact">
      {list.map((x) => {
        const live = livePresence(x);
        const online = live.presence !== 'offline';
        return (
          <div key={x.id} class={`frow ${online ? 'is-online' : ''}`}>
            <div class="frow-who">
              <Avatar name={x.name} presence={live.presence} size={40} />
              <span class="frow-text">
                <b>{x.name}</b>
                <small class={`p-${live.presence}`}>{presenceLabel(live.presence, live.activity)}</small>
              </span>
            </div>
            <div class="frow-actions">
              {members.has(x.id) ? (
                <span class="frow-chip">In party</span>
              ) : (
                <AsyncButton class="sm green" disabled={!online} onClick={() => inviteToParty(x.id, x.name)}>
                  Invite
                </AsyncButton>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function openPartyInvite(): void {
  if (isOffline.value) return;
  openModal((close) => (
    <Modal title="Invite to party" icon={<Users size={22} />} size="md" onClose={close}>
      <PartyInvite close={close} />
    </Modal>
  ));
}
